// src/common/RequirePan.jsx

import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const needsSinglePan = (path) =>
  [
    "/wards",
    "/agents",
    "/house-master",
    "/house-details",
    "/user-permissions",

    "/settings/financial-year",
    "/settings/waterbill-settings",
    "/settings/general-settings",

    // ✅ SMS Gateway needs one Panchayath
    "/settings/sms-gateway",
  ].some((p) => path.startsWith(p));

export default function RequirePan({ children }) {
  const { pathname } = useLocation();

  const warnedRef = useRef(false);

  const role = String(
    useSelector((s) => s.auth.user?.role) || ""
  ).toLowerCase();

  const isSuperadmin =
    role === "superadmin";

  const panIds = useSelector(
    (s) => s.tenant.pan_ids || []
  );

  const panActive = useSelector(
    (s) => s.tenant.pan_id_active || 0
  );

  // =========================================================
  // CHECK IF CURRENT PAGE REQUIRES ONE PANCHAYATH
  // =========================================================

  const mustBeSingle = useMemo(() => {
    if (!isSuperadmin) return false;

    return needsSinglePan(pathname);
  }, [isSuperadmin, pathname]);

  // =========================================================
  // VALID SINGLE PANCHAYATH
  // =========================================================

  const okSingle = useMemo(() => {
    if (!mustBeSingle) return true;

    return (
      panIds.length === 1 &&
      Number(panActive) > 0
    );
  }, [
    mustBeSingle,
    panIds,
    panActive,
  ]);

  // =========================================================
  // WARNING
  // =========================================================

  useEffect(() => {
    if (!mustBeSingle) return;

    if (okSingle) {
      warnedRef.current = false;
      return;
    }

    if (!warnedRef.current) {
      warnedRef.current = true;

      toast.warning(
        "Please select a single Panchayath"
      );
    }
  }, [
    mustBeSingle,
    okSingle,
  ]);

  // =========================================================
  // NORMAL USERS
  // =========================================================

  if (!isSuperadmin) {
    return children;
  }

  // =========================================================
  // SUPERADMIN WITHOUT SINGLE PAN
  // =========================================================

  if (
    mustBeSingle &&
    !okSingle
  ) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  return children;
}